import { CSSResultGroup, TemplateResult, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './tufts-card-provider-listing.css.lit';
import tuftsHeadingCssLit from '../tufts-heading/tufts-heading.css.lit';
import { tuftsCard } from '../tufts-card/tufts-card';
import StyleTuftsButton from '../tufts-button/tufts-button.css.lit';

/**
 * The tuftsCardProviderListing component
 * @element tufts-card-provider-listing
 *
 * @slot photo
 * @slot name
 * @slot distance
 * @slot specialty
 * @slot subspecialty
 * @slot reviews
 * @slot location
 * @slot address
 * @slot phone
 * @slot accepting
 * @slot additional-health
 * @slot virtual
 * @slot partner
 * @slot profile-link
 * @slot schedule-link
 *
 * @attr custom-title-expertise
 */
@customElement('tufts-card-provider-listing')
export class tuftsCardProviderListing extends tuftsCard {
  static styles: CSSResultGroup = [
    componentStyles,
    StyleTuftsButton,
    tuftsHeadingCssLit,
  ];
  @property({
    type: String,
    reflect: true,
    attribute: 'custom-title-expertise',
  })
  customTitle = 'expertise';

  additionalLocationsTemplate(): TemplateResult | undefined {
    if (this.ngSlot.exist('additional-location')) {
      const additionalLocations = this.querySelectorAll(
        '[slot="additional-location"]'
      );
      return html`
        <details class="additional-location-wrapper">
          <summary>
            View <strong>${additionalLocations.length}</strong> additional
            locations
          </summary>
          ${this.ngSlot.conditionalNGSlot('additional-location')}
        </details>
      `;
    }
    return undefined;
  }
  additionalExpertiseTemplate(): TemplateResult | undefined {
    if (this.ngSlot.exist('additional-expertise')) {
      const additionalExpertise = this.querySelectorAll(
        '[slot="additional-expertise"]'
      );
      return html`
        <details class="additional-expertise-wrapper">
          <summary>
            View <strong>${additionalExpertise.length}</strong> more areas of
            ${this.customTitle}
          </summary>
          ${this.ngSlot.conditionalNGSlot('additional-expertise')}
        </details>
      `;
    }
    return undefined;
  }

  desktopTemplate(): TemplateResult {
    return html`
      <article class="wrapper">
        <div class="media-wrapper">
          ${this.ngSlot.conditionalNGSlot('photo')}
          ${this.ngSlot.conditionalNGSlot('reviews')}
        </div>
        <div class="content">
          <div class="title-wrapper">
            ${this.ngSlot.conditionalNGSlot('name', true, 'md')}
            ${this.ngSlot.conditionalNGSlot('distance')}
          </div>
          ${this.ngSlot.conditionalNGSlot('specialty')}
          ${this.ngSlot.conditionalNGSlot('subspecialty')}
          ${this.ngSlot.conditionalNGSlot('iconprint')}
          ${this.ngSlot.conditionalNGSlot('expertise')}
          ${this.additionalExpertiseTemplate()}
          <div class="information-wrapper">
            ${this.ngSlot.conditionalNGSlot('location')}
            ${this.ngSlot.conditionalNGSlot('address')}
            ${this.ngSlot.conditionalNGSlot('phone')}
            ${this.additionalLocationsTemplate()}
          </div>
          ${this.ngSlot.conditionalNGSlot('accepting')}
          ${this.ngSlot.conditionalNGSlot('additional-health')}
          ${this.ngSlot.conditionalNGSlot('virtual')}
          ${this.ngSlot.conditionalNGSlot('partner')}
          <div class="button-wrapper">
            ${this.ngSlot.conditionalNGSlot('profile-link')}
            ${this.ngSlot.conditionalNGSlot('schedule-link')}
          </div>
        </div>
      </article>
    `;
  }

  mobileTemplate(): TemplateResult {
    return html`
      <div class="mobile-wrapper">
        <div class="top-wrapper">
          ${this.ngSlot.conditionalNGSlot('photo')}
          <div class="doc-info">
            <div class="title-wrapper">
              ${this.ngSlot.conditionalNGSlot('name', true, 'md')}
              ${this.ngSlot.conditionalNGSlot('distance')}
            </div>
            ${this.ngSlot.conditionalNGSlot('specialty')}
            ${this.ngSlot.conditionalNGSlot('subspecialty')}
            ${this.ngSlot.conditionalNGSlot('expertise')}
            ${this.ngSlot.conditionalNGSlot('iconprint')}
            ${this.additionalExpertiseTemplate()}
          </div>
        </div>
        ${this.templateBottomWrapperMobile()}
      </div>
    `;
  }
  templateBottomWrapperMobile() {
    if (
      this.ngSlot.exist('reviews') ||
      this.ngSlot.exist('location') ||
      this.ngSlot.exist('address') ||
      this.ngSlot.exist('phone') ||
      this.ngSlot.exist('accepting') ||
      this.ngSlot.exist('additional-health') ||
      this.ngSlot.exist('virtual') ||
      this.ngSlot.exist('partner') ||
      this.ngSlot.exist('profile-link') ||
      this.ngSlot.exist('schedule-link')
    ) {
      return html`
        <div class="bottom-wrapper">
          ${this.ngSlot.conditionalNGSlot('reviews')}
          <div class="information-wrapper">
            ${this.ngSlot.conditionalNGSlot('location')}
            ${this.ngSlot.conditionalNGSlot('address')}
            ${this.ngSlot.conditionalNGSlot('phone')}
            ${this.additionalLocationsTemplate()}
          </div>
          ${this.ngSlot.conditionalNGSlot('accepting')}
          ${this.ngSlot.conditionalNGSlot('additional-health')}
          ${this.ngSlot.conditionalNGSlot('virtual')}
          ${this.ngSlot.conditionalNGSlot('partner')}
          <div class="button-wrapper">
            ${this.ngSlot.conditionalNGSlot('profile-link')}
            ${this.ngSlot.conditionalNGSlot('schedule-link')}
          </div>
        </div>
      `;
    }
    return null;
  }

  render(): TemplateResult {
    return html`
      ${this.resizeController.currentBreakpointRange === 0
        ? this.mobileTemplate()
        : this.desktopTemplate()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-card-provider-listing': tuftsCardProviderListing;
  }
}
